import { useHubLang, dateLocaleFor } from '../../lib/i18n'
import { WIKI } from '../../lib/hubCopy'
/**
 * A two-state toggle for a curator's row controls.
 *
 * ── WHY A TOGGLE AND NOT TWO BUTTONS ────────────────────────────────────────
 *
 * Publish/unpublish and official/not-official are each ONE property with two
 * values, and the row already knows which value it holds. Rendering them as
 * action buttons — a big blue "Publish" that becomes a quiet "Unpublish" —
 * had two costs on the 8 Sep screen:
 *
 *   1. THE ROW RESHUFFLED. A `btn--primary` Publish is wider and taller than
 *      the quiet buttons beside it, so acting on one row moved the controls on
 *      every other row. In a list of 26 drafts that means the button you were
 *      aiming at is somewhere else by the time you look back.
 *
 *   2. IT STATED THE ACTION AND NOT THE STATE. "Publish" tells you what will
 *      happen if you click; it does not tell you where the item stands, which
 *      is what a curator scanning a list actually needs. A toggle shows both:
 *      the filled half is the truth, the other half is the offer.
 *
 * Both halves are always rendered at the same width, so nothing moves when the
 * state flips. That is the fix for (1) and it is not cosmetic — a control that
 * relocates under the cursor is how the wrong row gets published.
 *
 * ── IT IS OPTIMISTIC ABOUT NOTHING ──────────────────────────────────────────
 *
 * The caller re-reads the list after every write (028c: a zero-row UPDATE
 * under RLS returns success, so a spliced-in state change can show something
 * that never happened). While that is in flight both halves are disabled and
 * the pending one is marked, so a second click cannot queue a second write.
 */
export function RowToggle({
  label,
  value,
  options,
  busy,
  disabled,
  onPick,
}: {
  /** Announced to screen readers; the halves alone do not say what they toggle. */
  label: string
  value: string
  /** Exactly two. A third value is a different control — see the pills. */
  options: [{ key: string; text: string; title?: string }, { key: string; text: string; title?: string }]
  busy?: boolean
  disabled?: boolean
  onPick: (key: string) => void
}) {
  return (
    <span className="rowtoggle" role="group" aria-label={label}>
      {options.map((o) => {
        const on = o.key === value
        return (
          <button
            key={o.key}
            type="button"
            className={`rowtoggle__b${on ? ' rowtoggle__b--on' : ''}`}
            aria-pressed={on}
            // Clicking the half that is already true is a no-op rather than a
            // write. Sending it anyway would put a redundant verdict row under
            // the content every time somebody double-checked the state.
            disabled={disabled || busy || on}
            title={o.title}
            onClick={() => onPick(o.key)}
          >
            {o.text}
          </button>
        )
      })}
    </span>
  )
}

/**
 * "Still true" — a validation, not a toggle, and it carries its date.
 *
 * ── WHY IT LOOKS DIFFERENT FROM THE TOGGLES BESIDE IT ───────────────────────
 *
 * The other two controls set a property that can be set back. This one records
 * that a named human looked at this answer on a date, and there is no
 * un-looking: `last_reviewed_at` only moves forward. A toggle would imply a
 * second state to return to, and there isn't one.
 *
 * ── THE DATE IS THE POINT, NOT THE BUTTON ───────────────────────────────────
 *
 * `last_reviewed_at` is the single column standing between Wiki One and the
 * stale-PDF problem it exists to replace (031). A control that just says
 * "Still true" with no date tells a curator nothing about whether the answer
 * needs them — so the date is rendered first and the button second, and when
 * nobody has ever confirmed it says so in words rather than showing an empty
 * space.
 *
 * "Not reviewed in the last year" is app.wiki_needs_review() in the database,
 * one definition shared with the curator filter (039). Do not compute a
 * threshold here.
 */
export function StillTrue({
  reviewedAt,
  isStale,
  busy,
  onConfirm,
}: {
  reviewedAt: string | null
  isStale: boolean
  busy?: boolean
  onConfirm: () => void
}) {
  const lang = useHubLang()
  const w = WIKI[lang]
  return (
    <span className={`stilltrue${isStale ? ' stilltrue--due' : ''}`}>
      <span className="stilltrue__when">
        {reviewedAt
          ? w.confirmedOn(
              new Date(reviewedAt).toLocaleDateString(dateLocaleFor(lang), {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              }),
            )
          : w.neverConfirmed}
      </span>
      <button
        type="button"
        className="stilltrue__go"
        disabled={busy}
        title={w.stillTrueTitle}
        onClick={onConfirm}
      >
        {w.stillTrue}
      </button>
    </span>
  )
}
